import { SecureRoute } from '@okta/okta-react';
import { Dictionary, keyBy } from 'lodash';
import React, { FC, useMemo } from 'react';
import { Switch, useRouteMatch } from 'react-router';
import { MemberType, Team, UserType } from 'api/models';
import { PageLoading } from 'components/atoms/loading';
import { groupByTransformer } from 'components/organisms/resource-management/transformers/group-by-transfomer';
import {
  ProjectsWithMembers,
  ProjectUser,
  TeamsGroupedByCompany,
  UsersGroupedByCompany,
  UserWithProjects,
} from 'components/organisms/resource-management/types';
import { DefaultWorkingDayConfig } from 'components/organisms/user-capacity';
import { ResourceManagementDemandPage } from 'components/pages/resource-management/demand';
import { useHash } from 'hooks/use-hash';
import { useGetTeamsByProjectId } from 'hooks/use-idb';
import { useProjectsMetaQuery, useProjectsQuery } from 'queries';
import { useActiveOrInvitedNonCustomerUsersQuery } from 'queries/members';
import { useGetAllActivePartnerCompanies } from 'queries/partner';
import {
  useNativeTeamsInAccount,
  usePlaceholderResolutionsQuery,
} from 'queries/placeholder';
import { useProjectUsersLiteQuery } from 'queries/project';
import { useResourceManagementConfigQuery } from 'queries/resource-management-configuration';
import { useVendorRolesQuery } from 'queries/roles';
import { NotFound } from '../404';
import { ResourceManagementViewSwitcher } from './view-switcher';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

interface ComputedValues {
  projectWithMembers: ProjectsWithMembers[];
  usersWithProjects: Dictionary<UserWithProjects>;
  teamMembership: Dictionary<Team[]>;
  teamIdsGroupedByCompany: TeamsGroupedByCompany;
}

export const ResourceManagementMainRoute: FC = () => {
  const [groupBy] = useHash(groupByTransformer);
  const { data: projects = EMPTY_ARRAY, isLoading: isProjectsLoading } =
    useProjectsQuery();
  const { data: projectsMeta = EMPTY_ARRAY, isLoading: isProjectsMetaLoading } =
    useProjectsMetaQuery();
  const {
    data: partnerCompanies = EMPTY_ARRAY,
    isLoading: isPartnerCompaniesLoading,
  } = useGetAllActivePartnerCompanies();
  const partnerCompaniesLookup = useMemo(
    () => keyBy(partnerCompanies, 'companyId'),
    [partnerCompanies],
  );
  const {
    data: nonCustomerActiveUsers = EMPTY_ARRAY,
    isLoading: isUsersLoading,
  } = useActiveOrInvitedNonCustomerUsersQuery();
  const {
    data: projectUsersLite = EMPTY_OBJECT,
    isLoading: isReplacedUsersLoading,
  } = useProjectUsersLiteQuery();
  const projectIds = useMemo(
    () => (projects ?? []).map((p) => p.projectId),
    [projects],
  );
  const {
    data: placeholderResolutionsByProject,
    isLoading: placeholderResolutionLoading,
  } = usePlaceholderResolutionsQuery();
  const userLookupByCompanyId = useMemo(() => {
    return nonCustomerActiveUsers.reduce(
      (lookup, { userId, userType, company: { companyId } }) => {
        if (userType === UserType.Partner) {
          const userIdsForCompany = lookup[companyId] ?? [];

          return {
            ...lookup,
            [companyId]: userIdsForCompany.concat(userId),
          };
        }

        return lookup;
      },
      {} as UsersGroupedByCompany,
    );
  }, [nonCustomerActiveUsers]);
  const teamsMap = useGetTeamsByProjectId(projectIds);

  const userLookup = useMemo(() => {
    if (isUsersLoading) {
      return undefined;
    }

    return keyBy(nonCustomerActiveUsers, 'userId');
  }, [isUsersLoading, nonCustomerActiveUsers]);

  const projectMetaLookup = useMemo(() => {
    if (isProjectsMetaLoading) {
      return undefined;
    }

    return keyBy(projectsMeta, 'projectId');
  }, [isProjectsMetaLoading, projectsMeta]);

  const computedValues: ComputedValues | undefined = useMemo(() => {
    if (
      userLookup == null ||
      projectMetaLookup == null ||
      isReplacedUsersLoading ||
      isProjectsLoading ||
      placeholderResolutionLoading ||
      teamsMap.isLoading
    ) {
      return undefined;
    }

    const usersWithProjects: Dictionary<UserWithProjects> =
      nonCustomerActiveUsers.reduce(
        (result, user) => ({
          ...result,
          [user.userId]: {
            user,
            projects: [],
          },
        }),
        {},
      );

    const temp: ComputedValues = {
      projectWithMembers: [],
      usersWithProjects: {},
      teamMembership: {},
      teamIdsGroupedByCompany: {},
    };

    projects.forEach((project) => {
      const projectMembers: ProjectUser[] = [];

      (projectUsersLite[project.projectId] ?? []).forEach(
        ({ user: { userId }, role, referenceTeam }) => {
          // projectWithMembers
          if (userLookup[userId]) {
            projectMembers.push({
              user: userLookup[userId],
              role,
              referenceTeam,
            });

            // usersWithProjects
            usersWithProjects[userId] = {
              user: userLookup[userId],
              projects: (usersWithProjects[userId]?.projects ?? []).concat({
                project,
                role,
                referenceTeam,
              }),
            };
          }
        },
      );

      temp.projectWithMembers = temp.projectWithMembers.concat({
        ...project,
        ...projectMetaLookup[project.projectId],
        projectMembers,
      });
      temp.usersWithProjects = {
        ...temp.usersWithProjects,
        ...usersWithProjects,
      };
      temp.teamMembership[project.projectId] = (
        teamsMap.data?.[project.projectId] ?? []
      )
        .filter((team) => team.teamType !== MemberType.External)
        .map((team) => {
          const resolvedWith =
            placeholderResolutionsByProject?.[project.projectId]?.mappings?.[
              team.teamId
            ];

          const teamWithResolvedData = {
            ...team,
            userList:
              resolvedWith?.identityType === 'USER'
                ? [resolvedWith]
                : undefined,
            companyList:
              resolvedWith?.identityType === 'COMPANY'
                ? [resolvedWith]
                : undefined,
            resolved: resolvedWith?.identityType === 'USER',
          };

          if (resolvedWith?.identityType === 'COMPANY') {
            temp.teamIdsGroupedByCompany[resolvedWith.companyId] = (
              temp.teamIdsGroupedByCompany[resolvedWith.companyId] ?? []
            ).concat({
              ...teamWithResolvedData,
              projectId: project.projectId,
            });
          }

          return teamWithResolvedData;
        });
    });

    return temp;
  }, [
    isProjectsLoading,
    isReplacedUsersLoading,
    nonCustomerActiveUsers,
    placeholderResolutionLoading,
    placeholderResolutionsByProject,
    projectMetaLookup,
    projectUsersLite,
    projects,
    teamsMap.data,
    teamsMap.isLoading,
    userLookup,
  ]);

  const projectLookup = useMemo(() => {
    if (computedValues != null) {
      return keyBy(computedValues.projectWithMembers, 'projectId');
    }

    return undefined;
  }, [computedValues]);

  const { data: resourceManagementConfig, isLoading: isConfigLoading } =
    useResourceManagementConfigQuery();
  const { data: teams = EMPTY_ARRAY, isLoading: isTeamsLoading } =
    useNativeTeamsInAccount();
  const { data: roles = EMPTY_ARRAY, isLoading: isRolesLoading } =
    useVendorRolesQuery();

  const roleLookup = useMemo(() => {
    if (isRolesLoading) {
      return undefined;
    }

    return keyBy(roles, 'roleId');
  }, [isRolesLoading, roles]);

  const teamLookup = useMemo(() => {
    if (isTeamsLoading) {
      return undefined;
    }

    return keyBy(teams, 'teamId');
  }, [isTeamsLoading, teams]);

  const { path } = useRouteMatch();

  if (
    isConfigLoading ||
    placeholderResolutionLoading ||
    isPartnerCompaniesLoading ||
    computedValues == null ||
    projectLookup == null ||
    userLookup == null ||
    teamLookup == null ||
    roleLookup == null
  ) {
    return <PageLoading css />;
  }

  return (
    <Switch>
      <SecureRoute path={`${path}/demand`} exact>
        <ResourceManagementDemandPage
          projects={computedValues.projectWithMembers}
          users={nonCustomerActiveUsers}
          roles={roles}
          teams={teams}
          projectLookup={projectLookup}
          userLookup={userLookup}
          roleLookup={roleLookup}
          teamLookup={teamLookup}
          teamMembership={computedValues.teamMembership}
          workingDaysConfig={DefaultWorkingDayConfig}
          resourceManagementConfig={resourceManagementConfig}
        />
      </SecureRoute>
      <SecureRoute path={path}>
        <ResourceManagementViewSwitcher
          groupBy={groupBy}
          projects={computedValues.projectWithMembers}
          users={nonCustomerActiveUsers}
          roles={roles}
          teams={teams}
          projectLookup={projectLookup}
          userLookup={userLookup}
          roleLookup={roleLookup}
          teamLookup={teamLookup}
          teamMembership={computedValues.teamMembership}
          usersWithProjects={computedValues.usersWithProjects}
          workingDaysConfig={DefaultWorkingDayConfig}
          resourceManagementConfig={resourceManagementConfig}
          partnerCompanyLookup={partnerCompaniesLookup}
          userIdsGroupedByCompany={userLookupByCompanyId}
          teamIdsGroupedByCompany={computedValues.teamIdsGroupedByCompany}
        />
      </SecureRoute>
      <SecureRoute path="*">
        <NotFound />
      </SecureRoute>
    </Switch>
  );
};
